import React, { useState, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import Loader from "../../../Shared/Components/Loader/Loader";
import "./PdfViewerScoped.css";
import "./PdfViewer.css";
import { pdfViewerConstant } from "../../../NewConstants/SourceDialogConstants";

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

// Constants
const DEFAULT_ZOOM = 1.5;
const MAX_ZOOM = 2.0;
const MIN_ZOOM = 0.5;
const ZOOM_STEP = 0.2;
const CONTAINER_PADDING = 40;
const MAX_CONTAINER_WIDTH = 800;
const PDF_WIDTH_PADDING = 100;
const MAX_PDF_WIDTH = 600;
const SCROLL_TIMEOUT = 100;
const MIN_PDF_WIDTH = 200;

const PdfViewer = ({ pdfUrl, open, onClose, page, selectedLanguage }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showPageJump, setShowPageJump] = useState(false);
  const [inputPage, setInputPage] = useState("");
  const [showPageJumpError, setShowPageJumpError] = useState(false);
  const [scale, setScale] = useState(DEFAULT_ZOOM);
  const [containerWidth, setContainerWidth] = useState(MAX_CONTAINER_WIDTH);
  const containerRef = useRef(null);
  const pageRefs = useRef([]);
  const observerRef = useRef(null);
  const didInitialScrollRef = useRef(false);
  const targetPageRef = useRef(1);
  const pageVisibilityRatiosRef = useRef({});

  // Extract page number from URL hash
  const getPageFromUrl = (url) => {
    if (!url) return 1;
    const hashMatch = url.match(/#page=(\d+)/);
    return hashMatch ? parseInt(hashMatch[1], 10) : (page || 1);
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(false);
    const pageFromUrl = getPageFromUrl(pdfUrl);
    targetPageRef.current = pageFromUrl;
    setCurrentPage(pageFromUrl);
    setInputPage(String(pageFromUrl));
    setScale(DEFAULT_ZOOM);
    setContainerWidth(
      Math.min(window.innerWidth - CONTAINER_PADDING, MAX_CONTAINER_WIDTH)
    );
    setNumPages(null);
    pageRefs.current = [];
    didInitialScrollRef.current = false;
    setShowPageJump(false);
    setShowPageJumpError(false);
    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
    }, SCROLL_TIMEOUT);
  }, [pdfUrl, open]);

  // Ensure currentPage isn't out of bounds if numPages changes
  useEffect(() => {
    if (numPages && currentPage > numPages) setCurrentPage(numPages);
  }, [numPages]);

  // --- IntersectionObserver sync for currentPage ---
  useEffect(() => {
    if (!numPages) return;
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    const container = containerRef.current;
    if (!container) return;
    // Reset visibility ratios whenever we re-create the observer.
    pageVisibilityRatiosRef.current = {};
    const options = {
      root: container,
      rootMargin: '0px',
      // Multiple thresholds so we get frequent updates while scrolling.
      threshold: [0, 0.1, 0.25, 0.5, 0.75, 0.9],
    };
    observerRef.current = new window.IntersectionObserver((entries) => {
      // While we are trying to deep-link / jump to a target page, don't let the observer force page 1.
      if (!didInitialScrollRef.current) return;
      // Update ratios for pages that changed
      entries.forEach((entry) => {
        const pageNum = Number(entry.target.getAttribute("data-page-number"));
        if (!pageNum) return;
        pageVisibilityRatiosRef.current[pageNum] = entry.intersectionRatio || 0;
      });

      // Compute "best page" across ALL known ratios (not just changed entries)
      let bestPage = null;
      let bestRatio = 0;
      const ratios = pageVisibilityRatiosRef.current;
      Object.keys(ratios).forEach((k) => {
        const pageNum = Number(k);
        const ratio = ratios[pageNum] || 0;
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestPage = pageNum;
        }
      });

      if (bestPage && bestRatio > 0) setCurrentPage(bestPage);
    }, options);
    pageRefs.current.forEach((ref) => {
      if (ref) observerRef.current.observe(ref);
    });
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [numPages, open, pdfUrl, scale, containerWidth]);

  // Update inputPage number if currentPage changes and modal is not open
  useEffect(() => {
    if (!showPageJump) setInputPage(String(currentPage));
  }, [currentPage, showPageJump]);

  // --- Manual Page Jump Logic ---
  function handlePageJump() {
    const pageNum = parseInt(inputPage, 10);
    if (!pageNum || pageNum < 1 || pageNum > numPages) {
      setShowPageJumpError(true);
      return;
    }
    setShowPageJump(false);
    setShowPageJumpError(false);
    targetPageRef.current = pageNum;
    didInitialScrollRef.current = false;
    setCurrentPage(pageNum);
    setTimeout(() => {
      if (pageRefs.current[pageNum - 1]) {
        window.requestAnimationFrame(() => {
          pageRefs.current[pageNum - 1].scrollIntoView({ behavior: "smooth", block: "start" });
          didInitialScrollRef.current = true;
        });
      }
    }, 0);
  }


  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setContainerWidth(
        Math.min(window.innerWidth - CONTAINER_PADDING, MAX_CONTAINER_WIDTH)
      );
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!open) return null;

  // Custom loading and error overlays
  const renderLoader = () => (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-20">
      <Loader />
      <div className="mt-4 text-lg font-medium">Loading PDF...</div>
    </div>
  );

  const renderError = () => (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-20">
      <div className="text-lg font-medium mb-2">Internet is not working.</div>
    </div>
  );

  // Handle document load success
  const onDocumentLoadSuccess = ({ numPages }) => {
    const clampedTarget = Math.min(Math.max(targetPageRef.current || 1, 1), numPages);
    targetPageRef.current = clampedTarget;
    setCurrentPage(clampedTarget);
    setInputPage(String(clampedTarget));
    setNumPages(numPages);
    setLoading(false);
    setError(false);
  };

  const onDocumentLoadError = (error) => {
    console.error("Error loading PDF:", error);
    setLoading(false);
    setError(true);
  };

  // Scroll when the desired page has finished rendering
  const onPageLoadSuccess = ({ pageNumber }) => {
    setLoading(false);
    const target = targetPageRef.current || 1;
    if (didInitialScrollRef.current) return;
    if (pageNumber !== target) return;
    if (!pageRefs.current[target - 1]) return;
    window.requestAnimationFrame(() => {
      pageRefs.current[target - 1].scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      didInitialScrollRef.current = true;
    });
  };

  // Zoom and scroll controls
  const zoomIn = () => {
    setScale((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  };

  const resetZoom = () => {
    setScale(DEFAULT_ZOOM);
  };

  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div
      className="pdf-viewer-modal fixed inset-0 z-50 w-full h-full bg-white flex flex-col"
      style={{ color: "var(--dark-neutral-text)" }}
    >
      {/* First Navigation Bar - Back Button */}
      <div className="flex items-center px-6 py-4 border-b border-gray-200 bg-white z-10">
        <div className="flex items-center">
          <button
            className="text-2xl font-bold text-gray-700 hover:text-black focus:outline-none"
            onClick={onClose}
            aria-label="Close PDF"
          >
           <img src="/Assets/Images/arrowLeftBlack.svg" alt="Back" className="w-6 h-6" />
          </button>
          <div className="text-lg font-semibold ml-4">
            {pdfViewerConstant.backToChat[selectedLanguage]}
          </div>
        </div>
      </div>

      {/* Second Navigation Bar - Zoom and Page Controls */}
      {numPages && (
        <div className="flex items-center justify-center px-6 py-3 border-b border-gray-200 bg-white z-10">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={zoomOut}
                className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium"
                disabled={scale <= MIN_ZOOM}
              >
                -
              </button>
              <button
                onClick={resetZoom}
                className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium"
              >
                {Math.round(scale * 100)}%
              </button>
              <button
                onClick={zoomIn}
                className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium"
                disabled={scale >= MAX_ZOOM}
              >
                +
              </button>
            </div>

            {/* --- Page Navigation Block --- */}
            <div className="flex items-center space-x-2 ml-8">
              {/* Page Indicator, clickable for page jump modal */}
              <div
                className="px-3 py-1 bg-gray-50 border border-gray-300 rounded font-mono text-base font-semibold cursor-pointer select-none"
                style={{ minWidth: 70, textAlign: 'center' }}
                title="Jump to page"
                onClick={() => setShowPageJump(true)}
              >
                {currentPage}/{numPages}
              </div>
              {/* Scroll to Top */}
              <button
                onClick={scrollToTop}
                className="ml-6 px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm font-medium"
              >
                ↑ Top
              </button>
            </div>

            {/* --- Page Jump Modal --- */}
            {showPageJump && (
              <div className="fixed inset-0 z-60 flex items-center justify-center overscroll-none">
                <div className="bg-white rounded shadow-md p-6 min-w-[220px] flex flex-col items-center" style={{minWidth:220}}>
                  <div className="mb-2 font-medium">{pdfViewerConstant.enterPageNumber[selectedLanguage]} (1–{numPages})</div>
                  <input
                    autoFocus
                    type="number"
                    min={1}
                    max={numPages}
                    value={inputPage}
                    onChange={e => setInputPage(e.target.value.replace(/[^\d]/g, ''))}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handlePageJump();
                      if (e.key === 'Escape') setShowPageJump(false);
                    }}
                    className="w-20 border border-gray-400 rounded py-1 px-2 mb-3 text-center"
                  />
                  <div className="w-full flex justify-between gap-2">
                    <button
                      className="px-3 py-1 rounded text-sm"
                      style={{border: "1px solid var(--primary-dark)"}}
                      onClick={handlePageJump}
                    >{pdfViewerConstant.go[selectedLanguage]}</button>
                    <button
                      className="px-3 py-1  rounded text-sm"
                      style={{border: "1px solid var(--primary-dark)"}}
                      onClick={() => setShowPageJump(false)}
                    >{pdfViewerConstant.cancel[selectedLanguage]}</button>
                  </div>
                  {showPageJumpError && (
                    <div className="text-red-600 mt-2 text-sm">{pdfViewerConstant.enterValidPageNumber[selectedLanguage]} (1–{numPages})</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* PDF Container */}
      <div className="flex-1 relative overflow-hidden">
        {loading && renderLoader()}
        {error && renderError()}
        <div
          ref={containerRef}
          className="w-full h-full overflow-y-auto relative"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={renderLoader()}
            error={renderError()}
            >
            {Array.from(new Array(numPages), (el, index) => (
              <div
                key={`page_${index + 1}`}
                ref={(el) => (pageRefs.current[index] = el)}
                data-page-number={index + 1}
              >
                <Page
                  pageNumber={index + 1}
                  scale={scale}
                  onLoadSuccess={onPageLoadSuccess}
                  className="shadow-lg mx-auto mb-4"
                  width={Math.max(
                    MIN_PDF_WIDTH,
                    Math.min(containerWidth - PDF_WIDTH_PADDING, MAX_PDF_WIDTH)
                  )}
                />
              </div>
            ))}
          </Document>
        </div>
      </div>
    </div>
  );
};

export default PdfViewer;